import { useState } from 'react'
import Drawer from '../Drawer'
import { Notice } from '../Toast'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { deletePlan, getAssessments, getPlanRows, getPrograms, savePlan } from '../api/endpoints'
import { ApiError } from '../api/client'
import type { AssessmentSummary, PlanRow, Program, ResultMode } from '../api/types'
import { ErrorNotice } from '../components'
import BandsDialog from './BandsDialog'

interface Option { versionId: number; label: string; kind: string }

/** The newest published version of each assessment, which is what a plan can use. */
function options(assessments: AssessmentSummary[]): Option[] {
  return assessments.flatMap((a) => {
    const v = a.versions.filter((x) => x.status === 'PUBLISHED').sort((x, y) => y.versionNo - x.versionNo)[0]
    return v ? [{ versionId: v.id, label: `${a.name} (v${v.versionNo})`, kind: a.kind }] : []
  })
}

const MODE_TEXT: Record<ResultMode, string> = { SINGLE: 'Each test is reported on its own', COMBINED: 'One combined result: aptitude score plus the questionnaire fit' }

/** Which tests a candidate takes in this campaign, and how their result is formed. A program can have its own plan; everyone else follows the general plan. */
export default function TestPlanEditor({ campaignId, programRequired }: { campaignId: number; programRequired: boolean }) {
  const queryClient = useQueryClient()
  const rows = useQuery({ queryKey: ['plan-rows', campaignId], queryFn: () => getPlanRows(campaignId) })
  const programs = useQuery({ queryKey: ['programs'], queryFn: getPrograms })
  const assessments = useQuery({ queryKey: ['assessments'], queryFn: getAssessments })
  const [editing, setEditing] = useState<{ programId: number | null } | null>(null)
  const [bands, setBandsFor] = useState<{ programId: number | null } | null>(null)
  const remove = useMutation({
    mutationFn: (programId: number | null) => deletePlan(campaignId, programId),
    onSuccess: async () => { await queryClient.invalidateQueries({ queryKey: ['plan-rows', campaignId] }); await queryClient.invalidateQueries({ queryKey: ['campaigns'] }) },
  })

  const groups = new Map<number | null, PlanRow[]>()
  for (const r of rows.data ?? []) groups.set(r.programId, [...(groups.get(r.programId) ?? []), r])
  const hasGeneral = groups.has(null)
  const programName = (id: number | null) => (id === null ? 'Everyone without a plan of their own' : programs.data?.find((p) => p.id === id)?.name ?? `Program ${id}`)
  const usable = options(assessments.data ?? [])

  return (
    <section className="card" aria-labelledby="plan-title">
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h2 id="plan-title">Tests and results</h2>
        <div className="row">
          {!hasGeneral && <button type="button" className="primary" onClick={() => setEditing({ programId: null })}>Set the general plan</button>}
          <button type="button" onClick={() => setEditing({ programId: -1 })}>Add a plan for a program</button>
        </div>
      </div>
      <p className="muted">
        {programRequired ? 'Candidates choose a program, and each program needs a plan.' : 'Candidates do not have to choose a program, so the general plan is what they follow.'} A program with its own plan follows that plan instead.
        Set the dates and attempts for each test under Schedule.
      </p>
      <ErrorNotice error={rows.error ?? remove.error} />
      {[...groups.entries()].map(([programId, items]) => (
        <div key={String(programId)} className="plan-group">
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <b>{programName(programId)}</b>
            <div className="row">
              <button type="button" onClick={() => setEditing({ programId })}>Change</button>
              <button type="button" onClick={() => setBandsFor({ programId })}>Admission bands</button>
              <button type="button" className="danger" disabled={remove.isPending} onClick={() => { if (window.confirm('Remove this plan?')) remove.mutate(programId) }}>Remove</button>
            </div>
          </div>
          <p className="muted" style={{ margin: '2px 0 6px' }}>{MODE_TEXT[items[0].resultMode]}</p>
          <ol className="plan-steps">{items.map((r) => <li key={r.id}><b>{r.assessmentName}</b> <span className="badge">{r.kind === 'APTITUDE' ? 'aptitude' : 'questionnaire'}</span> <span className="muted">v{r.versionNo}</span></li>)}</ol>
        </div>
      ))}
      {rows.data?.length === 0 && <div className="notice info">No tests are planned yet. Candidates cannot start until you add a plan. Create assessments under Content and publish them first.</div>}
      {editing && <PlanDialog campaignId={campaignId} programs={programs.data ?? []} usedPrograms={[...groups.keys()]} options={usable} existing={editing.programId === -1 ? undefined : groups.get(editing.programId)} initialProgram={editing.programId}
        onClose={() => setEditing(null)} onSaved={() => { setEditing(null); void queryClient.invalidateQueries({ queryKey: ['plan-rows', campaignId] }); void queryClient.invalidateQueries({ queryKey: ['campaigns'] }) }} />}
      {bands && <BandsDialog title={`Admission bands: ${programName(bands.programId)}`} programId={bands.programId} cycleId={campaignId} onClose={() => setBandsFor(null)} />}
    </section>
  )
}

function PlanDialog({ campaignId, programs, usedPrograms, options: opts, existing, initialProgram, onClose, onSaved }: {
  campaignId: number; programs: Program[]; usedPrograms: (number | null)[]; options: Option[]; existing?: PlanRow[]; initialProgram: number | null; onClose: () => void; onSaved: () => void
}) {
  const [programId, setProgramId] = useState<number | null>(initialProgram === -1 ? null : initialProgram)
  const choosingProgram = initialProgram === -1
  const [mode, setMode] = useState<ResultMode>(existing?.[0]?.resultMode ?? 'SINGLE')
  const [chosen, setChosen] = useState<number[]>(existing?.map((r) => r.assessmentVersionId) ?? [])
  const [adding, setAdding] = useState('')
  const save = useMutation({
    mutationFn: () => savePlan(campaignId, {
      programId, resultMode: mode,
      items: chosen.map((id) => { const old = existing?.find((r) => r.assessmentVersionId === id); return { assessmentVersionId: id, opensAt: old?.opensAt ?? null, closesAt: old?.closesAt ?? null, maxAttempts: old?.maxAttempts ?? 1, deliveryMode: old?.deliveryMode ?? null } }),
    }),
    onSuccess: onSaved,
  })
  const errs = save.error instanceof ApiError ? save.error.fields : {}
  const label = (id: number) => opts.find((o) => o.versionId === id)?.label ?? existing?.find((r) => r.assessmentVersionId === id)?.assessmentName ?? `Version ${id}`
  const move = (i: number, d: number) => { const c = [...chosen]; [c[i], c[i + d]] = [c[i + d], c[i]]; setChosen(c) }
  const free = opts.filter((o) => !chosen.includes(o.versionId))
  const available = programs.filter((p) => !usedPrograms.includes(p.id))
  return (
    <Drawer title={`${existing ? 'Change the plan' : 'New plan'}`} width={640} onClose={onClose} onSubmit={() => { save.mutate() }}
      footer={<><button type="button" onClick={onClose}>Cancel</button><button type="submit" className="primary" disabled={save.isPending || chosen.length === 0 || (choosingProgram && programId === null)}>{save.isPending ? 'Saving…' : 'Save plan'}</button></>}>
        {choosingProgram && (
          <label className="field">Program
            <select required value={programId ?? ''} onChange={(e) => setProgramId(e.target.value ? Number(e.target.value) : null)}>
              <option value="">Choose a program</option>{available.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </label>
        )}
        <fieldset className="question-set" style={{ border: 0, padding: 0 }}>
          <legend><b>How is the result formed?</b></legend>
          <label className="row"><input type="radio" name="mode" checked={mode === 'SINGLE'} onChange={() => setMode('SINGLE')} /> <span><b>Each test on its own.</b> Every test gets its own result and report page. Use this for a single test, or tests that should not be added together.</span></label>
          <label className="row"><input type="radio" name="mode" checked={mode === 'COMBINED'} onChange={() => setMode('COMBINED')} /> <span><b>One combined result.</b> Needs exactly one aptitude test and one questionnaire; the questionnaire fit for the program adds to the aptitude score.</span></label>
        </fieldset>
        {errs.resultMode && <Notice tone="error">{errs.resultMode}</Notice>}
        <h3>Tests, in the order candidates take them</h3>
        <ol className="plan-steps">
          {chosen.map((id, i) => (
            <li key={id} className="row">
              <span style={{ flex: 1 }}>{label(id)}</span>
              <button type="button" aria-label={`Move ${label(id)} up`} disabled={i === 0} onClick={() => move(i, -1)}>↑</button>
              <button type="button" aria-label={`Move ${label(id)} down`} disabled={i === chosen.length - 1} onClick={() => move(i, 1)}>↓</button>
              <button type="button" aria-label={`Remove ${label(id)}`} onClick={() => setChosen(chosen.filter((c) => c !== id))}>Remove</button>
            </li>
          ))}
        </ol>
        <div className="row">
          <select aria-label="Add a test" value={adding} onChange={(e) => { if (e.target.value) { setChosen([...chosen, Number(e.target.value)]); setAdding('') } }}>
            <option value="">Add a test…</option>{free.map((o) => <option key={o.versionId} value={o.versionId}>{o.label} · {o.kind === 'APTITUDE' ? 'aptitude' : 'questionnaire'}</option>)}
          </select>
        </div>
        <p className="muted">Only published assessments are listed. Publish a version under Content first.</p>
        {errs.items && <Notice tone="error">{errs.items}</Notice>}
        <ErrorNotice error={Object.keys(errs).length === 0 ? save.error : null} />
    </Drawer>
  )
}
